import { useCallback, useEffect, useRef, useState } from 'react'
import useEventListener from '../hooks/useEventListener'
import { useAppDispatch } from '../store/hooks'
import { closePopUpPromotion, PopUpWrapper, resetShown, showPopUpPromotion } from '../features/popUpListSlice'
import useInactivityUser from '../hooks/useInactivityUser'
import { useModalContentHeight } from '../hooks/useModalContentHeight'
import {
   FrequencyUnitType,
   PromotionHeadBand,
   PromotionNotification,
   PromotionPopup,
   PromotionSideBar,
   Portal,
   PopUpPromotionType,
} from 'booker'
import { Modal } from 'ui'
import useTime from '../hooks/useTime'
import { useScrollThreshold } from '../hooks/useScrollThreshold'
import classNames from 'classnames'
import {
   parseUtmToRecords,
   isPopupShownViaCookie,
   isPopupViewStateActive,
   setPopupShownViaCookie,
   PopupViewState,
   getSecondsByUnit,
   isFrequencyConfirmed,
   isWithinLocalConfirmationWindow,
   shouldResetFrequencyState,
} from 'booker/utils'
import { clickPopup } from '../api/popUpPromotion'
import { fetchPopupShownFromBackend, recordPopupShownToBackend } from '../api/popUpViews'

type Props = {
   onApplyPromo: (
      promoCode: string | null,
      promotion?: PopUpPromotionType,
      extraParams?: Record<string, string>,
   ) => void
   popUpWrapper: PopUpWrapper
   account?: string
   locale: string
   visitorToken: string
}

const PopUp = (props: Props) => {
   const { onApplyPromo, popUpWrapper, account, locale, visitorToken } = props
   const { value: popUp } = popUpWrapper
   const [isAlmostExit] = useEventListener()
   const isInactivity = useInactivityUser(popUp.configuration?.triggers.inactivity.seconds || null)
   const timePassed = useTime(
      popUp.configuration?.triggers.afterTimeElapsed.seconds
         ? popUp.configuration.triggers.afterTimeElapsed.seconds * 1000
         : null,
   )
   const hasScrollPassed = useScrollThreshold(popUp.configuration?.triggers.scroll.percentage)

   const dispatch = useAppDispatch()

   const [backendViewState, setBackendViewState] = useState<PopupViewState | undefined>(undefined)
   const isShownViaBackend = isPopupViewStateActive(backendViewState)
   const isFrequencyLimited =
      popUp.configuration?.frequency.frequencyType === 'SHOW_AFTER_TIME' ||
      popUp.configuration?.frequency.frequencyType === 'SHOW_ONCE'

   useEffect(() => {
      if (!isFrequencyLimited) return
      fetchPopupShownFromBackend({ popupId: popUp.id, visitorToken, account, locale }).then((state) => {
         setBackendViewState(state)
      })
   }, [popUp.id, isFrequencyLimited, visitorToken, account, locale])

   const { isContentTallerThanViewport, contentRef } = useModalContentHeight(
      popUpWrapper.isOpen && !popUpWrapper.hasBeenShown,
   )

   // memoria local de la última confirmación de "mostrado" (timestamp de expiración, o Infinity para SHOW_ONCE).
   // evita que el guard de resync de abajo dispare un resetShown prematuro: setBackendViewState no se refleja
   // hasta el próximo render, así que sin esto el efecto de resync (declarado antes que el que la actualiza)
   // siempre la lee stale en el mismo commit donde se abre/cierra el popup.
   const locallyConfirmedUntilRef = useRef<number | null>(null)

   function hidePopUp() {
      dispatch(closePopUpPromotion(popUp.id))
   }

   const setToSession = useCallback(
      (id: number, interval?: number, unit?: FrequencyUnitType) => {
         if (interval && unit) {
            const seconds = interval * getSecondsByUnit(unit)
            const expiredAt = new Date(new Date().getTime() + 1000 * seconds)
            setPopupShownViaCookie(id, expiredAt, account)
            recordPopupShownToBackend({ popupId: id, visitorToken, expiredAt, account, locale })
            setBackendViewState({ shown: true, expiresAt: expiredAt.toISOString() })
            locallyConfirmedUntilRef.current = expiredAt.getTime()
         } else {
            setPopupShownViaCookie(id, null, account)
            recordPopupShownToBackend({ popupId: id, visitorToken, expiredAt: null, account, locale })
            setBackendViewState({ shown: true, expiresAt: null })
            locallyConfirmedUntilRef.current = Infinity
         }
      },
      [visitorToken, account, locale],
   )

   // Debe ejecutarse ANTES que el efecto de guards de abajo: cuando se cierra el popup justo
   // después de que la ventana de confirmación ya venció, este efecto refresca
   // locallyConfirmedUntilRef en el mismo commit — si el orden fuera al revés, el guard leería
   // el ref ya vencido y dispararía un resetShown prematuro (reapertura inmediata al cerrar).
   useEffect(() => {
      if (popUpWrapper.isOpen) {
         if (popUp.configuration?.frequency.frequencyType === 'SHOW_AFTER_TIME') {
            setToSession(
               popUp.id,
               popUp.configuration.frequency.showEveryInterval,
               popUp.configuration.frequency.frequencyUnitType,
            )
         } else if (popUp.configuration?.frequency.frequencyType === 'SHOW_ONCE') {
            setToSession(popUp.id)
         }
         if (popUp.configuration?.triggers.autoHide.enabled) {
            setTimeout(() => {
               dispatch(closePopUpPromotion(popUp.id))
            }, popUp.configuration.triggers.autoHide.seconds * 1000)
         }
      } else if (popUpWrapper.hasBeenShown && isFrequencyLimited && !isPopupShownViaCookie(popUp.id, account)) {
         // cookie expiró mientras el popup estaba abierto; refrescar al cerrar para evitar re-apertura inmediata
         if (popUp.configuration?.frequency.frequencyType === 'SHOW_AFTER_TIME') {
            const seconds =
               (popUp.configuration.frequency.showEveryInterval ?? 1) *
               getSecondsByUnit(popUp.configuration.frequency.frequencyUnitType)
            const expiredAt = new Date(Date.now() + seconds * 1000)
            setPopupShownViaCookie(popUp.id, expiredAt, account)
            recordPopupShownToBackend({ popupId: popUp.id, visitorToken, expiredAt, account, locale })
            setBackendViewState({ shown: true, expiresAt: expiredAt.toISOString() })
            locallyConfirmedUntilRef.current = expiredAt.getTime()
         } else if (popUp.configuration?.frequency.frequencyType === 'SHOW_ONCE') {
            setPopupShownViaCookie(popUp.id, null, account)
            recordPopupShownToBackend({ popupId: popUp.id, visitorToken, expiredAt: null, account, locale })
            setBackendViewState({ shown: true, expiresAt: null })
            locallyConfirmedUntilRef.current = Infinity
         }
      }
   }, [
      dispatch,
      popUpWrapper.isOpen,
      popUpWrapper.hasBeenShown,
      popUp.id,
      popUp.configuration?.frequency,
      popUp.configuration?.triggers.autoHide,
      setToSession,
      isFrequencyLimited,
      account,
      visitorToken,
      locale,
   ])

   useEffect(() => {
      if (popUp.configuration) {
         const cookieShown = isPopupShownViaCookie(popUp.id, account)
         if (isFrequencyConfirmed({ isFrequencyLimited, isPopupShownViaCookie: cookieShown, isShownViaBackend })) {
            return
         }
         if (
            shouldResetFrequencyState({
               isFrequencyLimited,
               hasBeenShown: popUpWrapper.hasBeenShown,
               isLocallyConfirmed: isWithinLocalConfirmationWindow(locallyConfirmedUntilRef.current),
               isPopupShownViaCookie: cookieShown,
               isShownViaBackend,
            })
         ) {
            dispatch(resetShown(popUp.id))
            return
         }
         if (popUpWrapper.hasBeenShown) {
            return
         }
         if (popUp.configuration.triggers.exitIntent && isAlmostExit) {
            dispatch(showPopUpPromotion(popUp.id))
         }
         if (popUp.configuration.triggers.inactivity.enabled && isInactivity) {
            dispatch(showPopUpPromotion(popUp.id))
         }
         if (popUp.configuration.triggers.afterTimeElapsed.enabled && timePassed) {
            dispatch(showPopUpPromotion(popUp.id))
         }
         if (popUp.configuration.triggers.scroll.enabled && hasScrollPassed) {
            dispatch(showPopUpPromotion(popUp.id))
         }
      }
   }, [
      dispatch,
      isAlmostExit,
      isInactivity,
      timePassed,
      hasScrollPassed,
      popUp.configuration,
      popUp.id,
      isFrequencyLimited,
      backendViewState,
      isShownViaBackend,
      popUpWrapper.hasBeenShown,
      account,
   ])

   function applyPromo(promotion: PopUpPromotionType, extraParams?: Record<string, string>) {
      onApplyPromo(promotion.code, promotion, extraParams)
      hidePopUp()
   }

   if (popUpWrapper.alreadyAplied && popUpWrapper.hasBeenShown) {
      return null
   }

   switch (popUp.presentationType) {
      case 'POPUP':
         return (
            <Modal
               visible={popUpWrapper.isOpen && !popUpWrapper.hasBeenShown}
               onClose={hidePopUp}
               containerClassName={classNames('tw-flex tw-items-start tw-justify-center', {
                  'md:tw-items-center': !isContentTallerThanViewport,
                  'md:tw-justify-start': popUp.design?.displayPositionType === 'CENTER_LEFT',
                  'md:tw-justify-end': popUp.design?.displayPositionType === 'CENTER_RIGHT',
               })}
               preventAutoCenter
               outerClassName={classNames('md:tw-w-[600px] lg:tw-w-[600px] md:tw-my-[30px]')}
               useTailwindPrefix
               portalClassName={'itm-promotion-portal'}
               size="md"
            >
               <PromotionPopup
                  ref={contentRef}
                  promotion={popUp.promotion}
                  design={popUp.design}
                  content={popUp.content}
                  photo={popUp.popupPhoto}
                  onApply={applyPromo}
                  onCancel={hidePopUp}
                  onButtonClick={() => {
                     clickPopup(popUp.id)
                  }}
                  useTailwindPrefix
                  extraParams={parseUtmToRecords(popUp.utm)}
               />
            </Modal>
         )
      case 'SIDE_PANEL':
         return (
            <Portal className={'itm-promotion-portal'}>
               <PromotionSideBar
                  containerClassName="tw-z-[9999]"
                  isVisible={popUpWrapper.isOpen && !popUpWrapper.hasBeenShown}
                  onClose={hidePopUp}
                  useTailwindPrefix
                  promotion={popUp.promotion}
                  design={popUp.design}
                  content={popUp.content}
                  photo={popUp.popupPhoto}
                  onApply={applyPromo}
                  onButtonClick={() => {
                     clickPopup(popUp.id)
                  }}
                  extraParams={parseUtmToRecords(popUp.utm)}
               />
            </Portal>
         )
      case 'NOTIFICATION':
         return (
            <Portal className={'itm-promotion-portal'}>
               <PromotionNotification
                  containerClassName="tw-z-[9999]"
                  isVisible={popUpWrapper.isOpen && !popUpWrapper.hasBeenShown}
                  onClose={hidePopUp}
                  useTailwindPrefix
                  promotion={popUp.promotion}
                  design={popUp.design}
                  content={popUp.content}
                  onApply={applyPromo}
                  onButtonClick={() => {
                     clickPopup(popUp.id)
                  }}
                  extraParams={parseUtmToRecords(popUp.utm)}
               />
            </Portal>
         )
      case 'ANNOUNCEMENT_BAR':
         return (
            <Portal className={'itm-promotion-portal'}>
               <PromotionHeadBand
                  containerClassName="tw-z-[9999]"
                  isVisible={popUpWrapper.isOpen && !popUpWrapper.hasBeenShown}
                  onClose={hidePopUp}
                  useTailwindPrefix
                  promotion={popUp.promotion}
                  content={popUp.content}
                  design={popUp.design}
                  onApply={applyPromo}
                  onButtonClick={() => {
                     clickPopup(popUp.id)
                  }}
                  extraParams={parseUtmToRecords(popUp.utm)}
               />
            </Portal>
         )
   }
   return null
}

export default PopUp
